import pegasus from "../../build/global/js/00-pegasus";

/**
 * allModals
 *
 * Component code for creating all modals on the page
 *
 *  Usage:
 *  Add link with the attr 'data-pegasus-modal'.
 *  Any values passed to the attr will be added as classes on the modal so it can be styled accordingly.
 *  To include modal navigation, pass the class 'Modal--with-nav' to the data attr. This class will be picked up and the nav will be built.
 *
 *  Example:
 *  <a href="#" data-pegasus-modal="Modal--team Modal--with-nav"></a>
 *
 * @since  2.0.0
 */
window.allModals = () => {

    return {
        init() {
            const _this = this;
            const modalSkeleton = pegasus.stringToHTML(document.getElementById('Modal__Skeleton').innerHTML);
            const modalLinks = _this.createModalLinks(document.querySelectorAll("[data-pegasus-modal]"));

            modalLinks.forEach((link, index) => {

                const modal = modalSkeleton.cloneNode(true);

                let cssClass = 'Modal',
                    modalNav;

                //check for classes to add
                if (link.dataset.pegasusModal.length) {

                    cssClass += ' ' + link.dataset.pegasusModal;

                    //if nav specified
                    if (link.dataset.pegasusModal.includes('Modal--with-nav')) {
                        const firstNavLink = modalLinks.filter((link) => link.dataset.pegasusModal.includes('Modal--with-nav')).shift();
                        const lastNavLink = modalLinks.filter((link) => link.dataset.pegasusModal.includes('Modal--with-nav')).pop();

                        modalNav = _this.buildModalNav(index, modalLinks, firstNavLink, lastNavLink);

                        if (modalNav) {
                            modal.querySelector('.Modal__navigation').append(modalNav);
                        }
                    }

                }

                //setup classes and id
                modal.className = cssClass;
                modal.id = link.dataset.pegasusModalId;

                //append modal template to page if it does not already exist
                if (!document.getElementById(link.dataset.pegasusModalId)) {
                    document.body.append(modal);
                }

                //if 4 or less, load modal straight away. More than 4 and an event will be attached in createModalLinks()

                //IMPORTANT ---- WE CANNOT DO THIS ON GIVE A GRAD DUE TO THE REDIRECTION ON AUTH FROM PORTAL CREATING INSTANT PAGE REDIRECTIONS

                /*if (modalLinks.length <= 4) {
                    setTimeout(function () {
                        _this.loadModalContent(link.href, link.dataset.pegasusModalId);
                    }, 1000);
                }*/

            });

            // Ensure unique event listeners
            window.removeEventListener('modalopen', _this.handleModalOpen);
            window.addEventListener('modalopen', _this.handleModalOpen.bind(_this));
        },

        doesLinkAlreadyExist(link, linksArray) {
            return linksArray.find((element) => element.href === link.href)
        },

        createModalLinks(links) {
            const _this = this;
            const modalLinks = [];

            if (links.length) {
                links.forEach((link, index) => {
                    // Remove URL modification - use original href
                    const existingLink = _this.doesLinkAlreadyExist(link, modalLinks);

                    //check for existing link and copy id if true
                    if (existingLink) {
                        link.setAttribute('data-pegasus-modal-id', existingLink.dataset.pegasusModalId);
                    } else {
                        link.setAttribute('data-pegasus-modal-id', `modal-${index}`);
                    }

                    //if there are 5 or more modals, add 'click' to load more
                    if (links.length > 4) {
                        link.addEventListener('click', (e) => {
                            e.preventDefault();
                            _this.loadModalContent(link.href, link.dataset.pegasusModalId);
                        });
                    }

                    //add event to emit 'modalopen' event for specified modal
                    link.addEventListener('click', _this.emitModalOpen.bind(_this));

                    //check we're not adding existing links.
                    if (!existingLink) modalLinks.push(link);
                });
            }

            return modalLinks;

        },

        emitModalOpen(e) {
            e.preventDefault();

            const modalID = e.currentTarget.dataset.pegasusModalId;
            const myEvent = new CustomEvent("modalopen", { detail: { modalID } });

            window.dispatchEvent(myEvent);
        },

        buildModalNav(currentIndex, allLinksArray, firstNavLink = '', lastNavLink = '') {
            const _this = this;
            
            const currentLink = allLinksArray[currentIndex];
            const isFirst = currentLink.href == firstNavLink?.href;
            const isLast = currentLink.href == lastNavLink?.href;

            let prevLinkHTML, nextLinkHTML;

            //check if we're at the start or end
            let prevLink = isFirst ? lastNavLink ?? '' : allLinksArray[currentIndex - 1];
            let nextLink = isLast ? firstNavLink ?? '' : allLinksArray[currentIndex + 1];

            //check if links are modals with navs
            if (prevLink !== '') prevLink = (prevLink.dataset.pegasusModal.includes('Modal--with-nav')) ? prevLink : '';
            if (nextLink !== '') nextLink = (nextLink.dataset.pegasusModal.includes('Modal--with-nav')) ? nextLink : '';

            if (prevLink !== '') {
                const target = prevLink.dataset.pegasusModalId;
                prevLinkHTML = `<a href="${prevLink.href}" class="prev-link arrow-btn" data-pegasus-modal-id="${target}">${prevLink.dataset?.pegasusModalPrevLinkText ?? 'Previous'}</a>`;
            } else {
                prevLinkHTML = `<span class="prev-link arrow-btn -disabled">Previous</span>`;
            }

            if (nextLink !== '') {
                const target = nextLink.dataset.pegasusModalId;
                nextLinkHTML = `<a href="${nextLink.href}" class="next-link arrow-btn" data-pegasus-modal-id="${target}">${nextLink.dataset?.pegasusModalNextLinkText ?? 'Next'}</a>`;
            } else {
                nextLinkHTML = `<span class="next-link arrow-btn -disabled">Next</span>`;
            }

            //create modalNav DOM element
            const modalNav = pegasus.stringToHTML(`<div class="links">${prevLinkHTML} ${nextLinkHTML}</div>`);

            //set up modal open and ajax events on modal nav links
            modalNav.querySelectorAll('[data-pegasus-modal-id]').forEach((link) => {
                link.addEventListener('click', (e) => {
                    e.preventDefault();
                    _this.emitModalOpen(e);
                    _this.loadModalContent(link.href, link.dataset.pegasusModalId);
                });
            });

            return modalNav;
        },

        copyEventListeners(srcElement, destElement) {
            const events = getEventListeners(srcElement);
            for (const [event, listeners] of Object.entries(events)) {
                listeners.forEach(listener => {
                    destElement.addEventListener(event, listener.listener, listener.useCapture);
                });
            }
        },

        loadModalContent(href, modalID) {
            pegasus.fetchContent(href).then((resp) => {

                if (!resp.ok) throw Error(resp.statusText);

                return resp.text();

            }).then((htmlResponse) => {

                // Create a temporary container to parse the HTML
                const parser = new DOMParser();
                const doc = parser.parseFromString(htmlResponse, 'text/html');
                
                // Remove header and footer if they exist
                doc.querySelector('header')?.remove();
                doc.querySelector('footer')?.remove();
                
                // Get the main content
                const mainContent = doc.querySelector('main') || doc.querySelector('.Post__Container');
                let modalHTML = mainContent ? mainContent.innerHTML : htmlResponse;

                const modalBody = document.querySelector(`#${modalID} .Modal__body`);

                //Moving footer into modal body
                const modalFooter = document.querySelector(`#${modalID} .Modal__footer`);
                if (modalFooter) {
                    const clonedFooter = modalFooter.cloneNode(true);
                
                    modalHTML += clonedFooter.outerHTML;                
                    modalFooter.remove();
                }

                modalBody.innerHTML = modalHTML;

                //Reattach event listeners
                modalBody.querySelectorAll('[data-pegasus-modal-id]').forEach((link) => {
                    link.addEventListener('click', this.emitModalOpen.bind(this));

                    link.addEventListener('click', (e) => {
                        e.preventDefault();
                        this.loadModalContent(link.href, link.dataset.pegasusModalId);
                    });
                });

            }).then(() => {
                //reinit cf7 forms
                if (wpcf7) {
                    const cf7Forms = document.querySelectorAll(`#${modalID} .wpcf7 > form`)
                    if (cf7Forms.length) cf7Forms.forEach((form) => wpcf7.init(form));
                }

            }).catch(error => {
                const modalBody = document.querySelector(`#${modalID} .Modal__body`),
                    errorContainer = modalBody.querySelector('.Modal__error');

                errorContainer.classList.remove('hidden');
                console.error(error);
            });

        },

        loadModalContentHandler(e) {
            e.preventDefault();
            const link = e.currentTarget;
            this.loadModalContent(link.href, link.dataset.pegasusModalId);
        },

        handleModalOpen(e) {
            const modalID = e.detail.modalID;
            document.querySelectorAll('.Modal').forEach(modal => {
                modal.classList.remove('is-open');
            });
            const modal = document.getElementById(modalID);
            if (modal) {
                modal.classList.add('is-open');
                document.body.classList.add('modal-open');
            }
        }
    }
}


/**
 * singleModal
 *
 * Component code for a single modal
 *
 * @since  2.0.0
 */
window.singleModal = () => {

    return {
        open: false,

        handleModalOpen(evt) {

            //only run on actual modals, not skeleton
            if (this.$el.classList.contains('ModalSkeleton')) return;

            document.body.classList.add('modal-open');
            this.open = this.$el.id === evt.detail.modalID;

        },

        handleModalClose() {

            document.body.classList.remove('modal-open');
            this.open = false;

        }

    }

}
